import { z } from 'zod';

/**
 * The creative judge's read of one ad, as data. `reviewAdCopy` has always produced these fields;
 * the engine used to flatten them into a `needs_review` warning string at its output boundary, and
 * a score parsed back out of that string is never shown to a person. This is the structured form
 * that crosses the boundary instead. The warning string is unchanged beside it.
 */
export const AD_REVIEW_VERDICTS = ['meets_bar', 'under_bar', 'generic'] as const;
export type AdReviewVerdict = (typeof AD_REVIEW_VERDICTS)[number];

export const StructuredAdReviewSchema = z
  .object({
    /** Hook score of the strongest opening line, 1 to 10, from the judge's anchored bands. */
    score: z.number().min(1).max(10),
    /** The score a copy must reach to stop being flagged (AD_COPY_HOOK_SCORE_BAR). */
    bar: z.number().min(1).max(10),
    /** `generic` wins over `under_bar`: a competitor could run the copy unchanged. */
    verdict: z.enum(AD_REVIEW_VERDICTS),
    isGeneric: z.boolean(),
    weakestLine: z.string().max(1_000),
    strongestLine: z.string().max(1_000),
    failsBecause: z.array(z.string().max(600)).max(12),
    unsupportedAssertions: z.array(z.string().max(600)).max(12),
  })
  .strict();
export type StructuredAdReview = z.infer<typeof StructuredAdReviewSchema>;

interface ReviewLike {
  hookScore: number;
  weakestLine: string;
  strongestLine: string;
  isGeneric: boolean;
  failsBecause: readonly string[];
  unsupportedAssertions?: readonly string[];
}

function clipList(items: readonly string[], limit: number, max: number): string[] {
  return items.slice(0, limit).map((item) => (item.length > max ? item.slice(0, max) : item));
}

/** Null in, null out: a review that did not run is never turned into a score. */
export function toStructuredAdReview(
  review: ReviewLike | null | undefined,
  bar: number,
): StructuredAdReview | null {
  if (!review) return null;
  const score = Math.min(10, Math.max(1, review.hookScore));
  const clip = (text: string) => (text.length > 1_000 ? text.slice(0, 1_000) : text);
  return {
    score,
    bar,
    verdict: review.isGeneric ? 'generic' : score < bar ? 'under_bar' : 'meets_bar',
    isGeneric: review.isGeneric,
    weakestLine: clip(review.weakestLine),
    strongestLine: clip(review.strongestLine),
    failsBecause: clipList(review.failsBecause, 12, 600),
    unsupportedAssertions: clipList(review.unsupportedAssertions ?? [], 12, 600),
  };
}

/** True when the read would flag the ad: under the bar, or generic. */
export function reviewIsFlagged(review: StructuredAdReview): boolean {
  return review.isGeneric || review.score < review.bar;
}
